import React, { useEffect, useState } from 'react';
import { Card, Form, Pagination, Table } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import { TrashFill } from 'react-bootstrap-icons';
import { PrediosService } from '../../../service/predios.service';
import { Predio } from '../../../service/types/types';
import usePagination from '../../../service/utils/usePagination';
import PredioModal from './PredioModal';
import ViewPredioModal from './ViewPredioModal';

interface PredioListProps {
  /** Se dispara cada vez que la lista se (re)carga; útil si un consumidor externo también necesita los datos crudos (ej. un mapa). */
  onLoaded?: (predios: Predio[]) => void;
  /** Si se entrega, agrega un botón por fila para "seleccionar" ese predio. */
  onSelect?: (predio: Predio) => void;
  selectLabel?: string;
  /** Permite a un consumidor agregar botones extra por fila (ej. "Usuarios cercanos") sin acoplar esa lógica aquí. */
  renderRowActions?: (predio: Predio) => React.ReactNode;
}

export default function PredioList({ onLoaded, onSelect, selectLabel = 'Seleccionar', renderRowActions }: PredioListProps) {
  const [predios, setPredios] = useState<Predio[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const filtered = predios.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));
  const { currentPage, totalPages, nextPage, prevPage, currentData, resetPage } = usePagination(filtered, 10);

  const load = async () => {
    setLoading(true);
    try {
      const data = await PrediosService.findAll();
      setPredios(data);
      onLoaded?.(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (predio: Predio) => {
    if (!window.confirm(`¿Eliminar el predio "${predio.name}"? Esta acción no se puede deshacer.`)) return;
    await PrediosService.remove(predio.id);
    await load();
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Predios</h5>
        <PredioModal onSaved={load} />
      </Card.Header>
      <Card.Body>
        <Form.Control
          type="text"
          placeholder="Buscar por nombre..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetPage();
          }}
          className="mb-3"
        />
        <Table striped bordered hover responsive className="align-middle">
          <thead className="table-light">
            <tr>
              <th>Nombre</th>
              <th className="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {currentData().map((predio) => (
              <tr key={predio.id}>
                <td>{predio.name}</td>
                <td className="text-center">
                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    {onSelect && (
                      <Button size="sm" variant="outline-primary" onClick={() => onSelect(predio)}>
                        {selectLabel}
                      </Button>
                    )}
                    <ViewPredioModal predio={predio} />
                    <PredioModal predio={predio} onSaved={load} />
                    {renderRowActions?.(predio)}
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(predio)}>
                      <TrashFill className="me-1" /> Eliminar
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && currentData().length === 0 && (
              <tr>
                <td colSpan={2} className="text-center text-muted">
                  No hay predios
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        {totalPages > 1 && (
          <Pagination className="justify-content-center">
            <Pagination.Prev onClick={prevPage} disabled={currentPage === 1} />
            <Pagination.Item disabled>{`${currentPage} / ${totalPages}`}</Pagination.Item>
            <Pagination.Next onClick={nextPage} disabled={currentPage === totalPages} />
          </Pagination>
        )}
      </Card.Body>
    </Card>
  );
}
